import { useState, useEffect } from "react";
import { homeFor } from "@/lib/access";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { ACCESS_WORD, grantAccess, hasAccess } from "@/lib/accessKey";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { HardHat, ChevronRight, Radio, KeyRound } from "lucide-react";
import { toast } from "sonner";

const APP_VERSION = "v2.1.0 · Professional Operations";
const REMEMBER_KEY = "armorbatch_remember_email";
// lucide "shield" outline path — drawn as a brushed-steel plate with an Armor Blue rim
const SHIELD_PATH = "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Arriving on a private access link unlocks this device for good.
  const keyed = params.get("key") === ACCESS_WORD;
  if (keyed) grantAccess();

  // Hydrate remembered email
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setEmail(saved);
        setRemember(true);
      }
    } catch { /* ignore */ }
  }, []);

  // No key, never signed in here — this device belongs to the public journey.
  if (user === false && !keyed && !hasAccess()) return <Navigate to="/welcome" replace />;

  if (user && user !== false) return <Navigate to={user.demo_only ? "/war-room" : user.role === "driver" ? "/driver" : user.role === "loader" ? "/loader" : "/"} replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, email);
        else localStorage.removeItem(REMEMBER_KEY);
      } catch { /* ignore */ }
      toast.success(`Welcome, ${u.name}`);
      const pending = localStorage.getItem("armorbatch.pendingInvite");
      if (pending) { localStorage.removeItem("armorbatch.pendingInvite"); nav(`/partner-invite/${pending}`); return; }
      nav(u.demo_only ? "/war-room" : homeFor(u));
    } catch (e) {
      // Surface the real error to the operator — "Something went wrong"
      // hides CORS / network problems and makes lockouts un-debuggable.
      const detail = e.response?.data?.detail;
      let msg;
      if (detail != null) msg = formatApiErrorDetail(detail);
      else if (e.message && e.message !== "Network Error") msg = e.message;
      else msg = "Cannot reach the server. Check your connection or try again in a moment.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white text-slate-900" data-testid="login-page" style={{ "--armor": "#0052CC", "--armor-dark": "#003F9E" }}>
      {/* Left: airy white login panel · Armor Blue accents · form centred on the shield */}
      <div className="relative flex flex-col items-center px-8 py-10 lg:px-16 lg:py-12 overflow-hidden bg-white">
        <div className="w-full max-w-md text-center mb-10" data-testid="login-brand">
          <div className="heading text-3xl lg:text-4xl font-black uppercase tracking-tight leading-none" style={{ color: "var(--armor)" }} data-testid="login-brand-name">
            ArmorBatch Global
          </div>
          <div className="mono text-[10px] uppercase tracking-[0.35em] text-slate-400 mt-2" data-testid="login-brand-slogan">The Global Standard</div>
        </div>

        {/* Headline + shield group, vertically centred as one block */}
        <div className="relative w-full max-w-lg flex-1 flex flex-col justify-center items-center">
          <div className="text-center mb-4">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-4">SECURE LOGIN</div>
            <h1 className="heading text-4xl lg:text-5xl font-black uppercase mb-3 leading-none !text-slate-900">
              Pour with <span style={{ color: "var(--armor)" }}>precision.</span>
            </h1>
            <p className="text-slate-500 text-sm">
              Access batching, dispatch, delivery &amp; invoicing on one console.
            </p>
          </div>

          {/* Shield is sized to fully contain the fields with clear margin; the form is dead-centre inside it */}
          <div className="relative w-[520px] h-[560px] max-w-full flex items-center justify-center" data-testid="login-shield-wrap">
            <svg
              aria-hidden
              data-testid="login-shield"
              viewBox="0 0 24 24"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[660px] h-[660px] max-w-none pointer-events-none select-none"
              style={{ filter: "drop-shadow(0 18px 30px rgba(15,23,42,0.18))" }}
            >
              <defs>
                {/* Brushed-steel body: cool greys with a diagonal sheen */}
                <linearGradient id="steelBody" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#F1F3F6" />
                  <stop offset="0.28" stopColor="#D5DAE1" />
                  <stop offset="0.5" stopColor="#EEF1F4" />
                  <stop offset="0.72" stopColor="#C9CFD7" />
                  <stop offset="1" stopColor="#E4E8ED" />
                </linearGradient>
                <linearGradient id="steelSheen" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#FFFFFF" stopOpacity="0" />
                  <stop offset="0.45" stopColor="#FFFFFF" stopOpacity="0.55" />
                  <stop offset="0.55" stopColor="#FFFFFF" stopOpacity="0.55" />
                  <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
                </linearGradient>
                <pattern id="brush" width="0.18" height="24" patternUnits="userSpaceOnUse">
                  <rect width="0.06" height="24" fill="#0F172A" opacity="0.035" />
                </pattern>
                <clipPath id="shieldClip"><path d={SHIELD_PATH} /></clipPath>
              </defs>
              <path d={SHIELD_PATH} fill="url(#steelBody)" />
              <g clipPath="url(#shieldClip)">
                <rect x="0" y="0" width="24" height="24" fill="url(#brush)" />
                <rect x="0" y="0" width="24" height="24" fill="url(#steelSheen)" transform="rotate(-18 12 12)" />
                <path d="M4.6 6.2 L12 3.4 L12 3.9 L5.1 6.6 Z" fill="#FFFFFF" opacity="0.3" />
              </g>
              {/* Armor Blue rim */}
              <path d={SHIELD_PATH} fill="none" stroke="#0052CC" strokeWidth="0.55" strokeLinejoin="round" opacity="0.55" />
              <path d={SHIELD_PATH} fill="none" stroke="#FFFFFF" strokeWidth="0.12" strokeLinejoin="round" opacity="0.8" transform="translate(0 0.28) scale(0.985 0.985) translate(0.18 0.05)" />
            </svg>
            <form onSubmit={submit} className="relative w-[290px] space-y-4 text-left -translate-y-3">
              <div>
                <Label className="mono text-[10px] uppercase tracking-[0.25em] text-slate-600 mb-2 block">Email</Label>
                <Input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-xl bg-white border-slate-200 text-slate-900 mono h-11 px-4 shadow-sm focus-visible:border-[#0052CC] focus-visible:ring-4 focus-visible:ring-[#0052CC]/10 placeholder:text-slate-300"
                  required
                />
              </div>
              <div>
                <Label className="mono text-[10px] uppercase tracking-[0.25em] text-slate-600 mb-2 block">Password</Label>
                <Input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-xl bg-white border-slate-200 text-slate-900 mono h-11 px-4 shadow-sm focus-visible:border-[#0052CC] focus-visible:ring-4 focus-visible:ring-[#0052CC]/10"
                  required
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-wrap">
                  <Checkbox
                    data-testid="remember-me"
                    checked={remember}
                    onCheckedChange={(v) => setRemember(!!v)}
                    className="border-slate-300 bg-white data-[state=checked]:bg-[#0052CC] data-[state=checked]:border-[#0052CC]"
                  />
                  <span className="text-xs text-slate-600">Remember me</span>
                </label>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => setForgotOpen(true)}
                  className="text-xs mono uppercase tracking-widest hover:underline"
                  style={{ color: "var(--armor)" }}
                >
                  Forgot password?
                </button>
              </div>

              {err && (
                <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3 rounded-xl" data-testid="login-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit"
                disabled={busy}
                className="w-full h-12 rounded-xl inline-flex items-center justify-center gap-2 mono text-xs uppercase tracking-[0.25em] font-bold text-white shadow-lg shadow-[#0052CC]/25 transition-[background-color,transform] hover:-translate-y-px disabled:opacity-50 bg-[#0052CC] hover:bg-[#003F9E]"
              >
                {busy ? "Signing in…" : "Enter Console"} <ChevronRight className="w-4 h-4" />
              </button>
            </form>
          </div>

          <div className="relative mt-2 flex items-center justify-center gap-2 mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
            <Radio className="w-3.5 h-3.5" style={{ color: "var(--armor)" }} /> LIVE DISPATCH · GPS · PDF DOCKETS
          </div>
          <div className="relative mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1 text-[11px] mono uppercase tracking-widest">
            <a href="/driver/login" data-testid="login-to-driver-pin" className="text-slate-400 hover:text-[#0052CC]">Driver PIN login →</a>
            <a href="/portal/login" data-testid="login-to-customer-portal" className="text-slate-400 hover:text-[#0052CC]">Customer portal →</a>
            <a href="/contractor/login" data-testid="login-to-contractor-portal" className="text-slate-400 hover:text-[#0052CC]">Contractor portal →</a>
          </div>
        </div>

        <div className="relative w-full max-w-md text-center mono text-[10px] uppercase tracking-[0.3em] text-slate-400 pt-10" data-testid="login-version">
          © ARMORBATCH GLOBAL · {APP_VERSION}
        </div>
      </div>

      {/* Right: plant photo with a light Armor Blue wash */}
      <div className="hidden lg:block relative border-l border-slate-100" data-testid="login-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1621121013599-c94f85975cfd?crop=entropy&cs=srgb&fm=jpg&q=85')",
          }}
        />
        <div className="absolute inset-0 mix-blend-multiply" style={{ background: "linear-gradient(160deg, rgba(0,82,204,0.45) 0%, rgba(0,82,204,0.28) 50%, rgba(0,63,158,0.55) 100%)" }} data-testid="login-hero-overlay" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#002B6B]/80 via-transparent to-white/10" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-sky-200 mb-3">ARMORBATCH GLOBAL · SaaS</div>
          <div className="heading text-3xl font-black uppercase leading-tight max-w-md !text-white">
            Every truck. Every mix. Every m³.
          </div>
          <div className="mt-4 text-sm text-white/75 max-w-md">
            The complete concrete operations console — dispatch, batching, delivery dockets, invoices &amp; GPS in one modern interface.
          </div>
        </div>
      </div>

      {/* Forgot password — self-service reset flow */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-surface border-industrial max-w-md" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary-orange" />
              Password reset
            </DialogTitle>
          </DialogHeader>
          <ForgotPasswordForm onClose={() => setForgotOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// v3.5x — Self-service password reset. Emails a signed link to the user via
// Resend/SMTP. The backend endpoint returns 200 whether or not the email
// exists so we don't leak account existence.
function ForgotPasswordForm({ onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not send reset link");
    }
    setBusy(false);
  };

  if (sent) {
    return (
      <div className="space-y-3 text-sm">
        <div className="border border-emerald-500/40 bg-emerald-500/10 p-3">
          <div className="mono text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Check your inbox</div>
          <p className="text-ink text-[13px]">
            If <span className="mono">{email}</span> is on the system, a reset link is on its way. It expires in 2 hours.
          </p>
        </div>
        <p className="text-xs text-muted-industrial">
          Nothing arrived? Check your spam folder, then double-check the email spelling and try again.
        </p>
        <div className="flex justify-end pt-1">
          <button type="button" onClick={onClose} data-testid="forgot-close" className="mono text-xs uppercase tracking-widest text-muted-industrial hover:text-primary-orange">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-ink">
        Type the email you use to log in — we'll send a reset link.
      </p>
      <div>
        <Label className="text-[10px] tracking-widest uppercase text-muted-industrial">Email</Label>
        <Input
          data-testid="forgot-email"
          type="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-none border-industrial mono"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onClose} className="mono text-xs uppercase tracking-widest text-muted-industrial hover:text-primary-orange">
          Cancel
        </button>
        <Button data-testid="forgot-submit" type="submit" disabled={busy} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
          {busy ? "Sending…" : "Send Reset Link"}
        </Button>
      </div>
    </form>
  );
}
